/**
 * Shared debt constants and helpers used across pages and components.
 */

// ── Date helpers ──────────────────────────────────────────────────────────────

/**
 * Adds `months` months to an ISO date string (YYYY-MM-DD).
 * Uses UTC to avoid DST surprises. Adjusts overflow (e.g. Jan 31 + 1 → Feb 28).
 * Returns '' if iso is falsy.
 */
export function addMonthsISO(iso, months) {
  if (!iso) return '';
  const [y, m, d] = iso.split('-').map(Number);
  const date = new Date(Date.UTC(y, m - 1 + months, d));
  // If day overflowed (e.g. March 31 → April 31 → May 1), go back to last day of target month
  if (date.getUTCDate() !== d) {
    date.setUTCDate(0); // last day of previous month
  }
  return date.toISOString().slice(0, 10);
}

/**
 * Returns the number of whole months between two ISO date strings.
 * Result is always >= 0.
 */
export function monthsBetween(startISO, endISO) {
  if (!startISO || !endISO) return 0;
  const [y1, m1] = startISO.split('-').map(Number);
  const [y2, m2] = endISO.split('-').map(Number);
  return Math.max(0, (y2 - y1) * 12 + (m2 - m1));
}

// ── Newton-Raphson solver ─────────────────────────────────────────────────────

/**
 * Solves for the annual interest rate (in %) given:
 *   capital_cents         : loan principal in cents
 *   monthly_payment_cents : fixed monthly payment in cents
 *   n                     : number of repayment months (post-grace)
 *   g                     : grace period months (interest capitalised, no payment)
 *
 * Uses Newton-Raphson (forward-difference derivative) on
 *   f(r) = C·(1+r)^g · r / (1−(1+r)^−n) − M = 0
 * where r = monthly rate.
 *
 * Returns:
 *   - annual rate in % rounded to 2 decimal places (Number)
 *   - 0 if M·n <= C (no positive rate possible — mensualité ne couvre pas le capital)
 *   - null if no convergence after 50 iterations (display "—" in UI)
 */
export function solveAnnualRateClient(capital_cents, monthly_payment_cents, n, g = 0) {
  const C = capital_cents;
  const M = monthly_payment_cents;
  const G = Number(g) || 0;

  if (!C || !M || !n || n <= 0) return null;
  if (M * n <= C) return 0;
  if ((M * n - C) / C < 1e-6) return 0;

  let r = 0.005;

  for (let i = 0; i < 50; i++) {
    const onePlusR = 1 + r;
    const graceFactor = Math.pow(onePlusR, G);
    const denom = 1 - Math.pow(onePlusR, -n);
    if (Math.abs(denom) < 1e-12) return null;

    const f = C * graceFactor * r / denom - M;

    // Forward-difference derivative
    const dr = Math.max(r * 1e-6, 1e-10);
    const r2 = r + dr;
    const denom2 = 1 - Math.pow(1 + r2, -n);
    const f2 = C * Math.pow(1 + r2, G) * r2 / denom2 - M;
    const df = (f2 - f) / dr;
    if (Math.abs(df) < 1e-12) return null;

    const rNext = r - f / df;
    if (rNext <= 0) {
      r = r / 2;
      continue;
    }
    if (Math.abs(rNext - r) < 1e-9) {
      return Math.round(rNext * 12 * 100 * 100) / 100;
    }
    r = rNext;
  }

  return null;
}

/**
 * Preview: computes the monthly payment (in cents) given capital, annual rate,
 * duration (repayment months) and optional grace period.
 * Formula: M = C·(1+r)^g · r / (1 − (1+r)^−n), r = annual_rate_pct/12/100.
 * Returns null if parameters are invalid.
 */
export function computeMonthlyPaymentCents(capital_cents, annual_rate_pct, n, g = 0) {
  if (!capital_cents || capital_cents <= 0 || !n || n <= 0) return null;
  const r = (annual_rate_pct ?? 0) / 12 / 100;
  const G = Number(g) || 0;
  if (r <= 0) return Math.round(capital_cents / n);
  const capitalAfterGrace = capital_cents * Math.pow(1 + r, G);
  const payment = capitalAfterGrace * r / (1 - Math.pow(1 + r, -n));
  return Math.round(payment);
}

// ── Revolving / tirage progressif helpers ─────────────────────────────────────

/**
 * Previews the monthly provision amount for a single drawdown.
 *
 * Logic mirrors the backend exactly:
 *   firstOf      = iso => iso.slice(0,7)+'-01'
 *   startRaw     = firstOf(drawnDate) + 1 month
 *   tMonth       = firstOf(plannedDate)
 *   provStart    = startRaw <= tMonth ? startRaw : tMonth
 *   periods      = max(1, monthsBetween(provStart, tMonth) + 1)
 *   monthly      = round(amount / periods)
 *   provEnd      = tMonth
 *
 * @param {number} amountCents   - Amount drawn, in cents.
 * @param {string} drawnDateISO  - Draw date, 'YYYY-MM-DD'.
 * @param {string} plannedISO    - Planned repayment date, 'YYYY-MM-DD'.
 * @returns {{ monthly_provision_cents, periods_remaining, provision_start, provision_end }}
 */
export function computeDrawdownProvisionClient(amountCents, drawnDateISO, plannedISO) {
  const firstOf = (iso) => iso.slice(0, 7) + '-01';
  const startRaw = addMonthsISO(firstOf(drawnDateISO), 1);
  const tMonth = firstOf(plannedISO);
  const provision_start = startRaw <= tMonth ? startRaw : tMonth;
  const periods = Math.max(1, monthsBetween(provision_start, tMonth) + 1);
  const monthly_provision_cents = Math.round(amountCents / periods);
  return {
    monthly_provision_cents,
    periods_remaining: periods,
    provision_start,
    provision_end: tMonth,
  };
}

/**
 * Estimates capitalized interest accrued between two dates.
 * Formula: capital × ((1 + r/12)^k − 1), k = monthsBetween(start, at).
 * Returns 0 if k <= 0 or rate <= 0.
 *
 * @param {number} capitalCents    - Base capital in cents.
 * @param {number} annualRatePct   - Annual interest rate in percent (e.g. 6).
 * @param {string} startISO        - Start date 'YYYY-MM-DD'.
 * @param {string} atISO           - Target date 'YYYY-MM-DD'.
 * @returns {number} Interest in cents, rounded.
 */
export function computeCapitalizedInterestClient(capitalCents, annualRatePct, startISO, atISO) {
  const k = monthsBetween(startISO, atISO);
  if (k <= 0 || !annualRatePct || annualRatePct <= 0) return 0;
  const r = annualRatePct / 100;
  return Math.round(capitalCents * (Math.pow(1 + r / 12, k) - 1));
}

export const INTEREST_MODE_LABELS = {
  serviced:    'Intérêts servis',
  capitalized: 'Intérêts capitalisés (in fine)',
};

export const SOURCE_TYPE_LABELS = {
  bank_loan:           'Prêt bancaire',
  pge:                 'PGE',
  shareholder_advance: 'Apport associé',
  private_investor:    'Investisseur privé',
  supplier_credit:     'Crédit fournisseur',
  other:               'Autre',
};

// Tailwind badge classes per source_type
export const SOURCE_TYPE_BADGE = {
  bank_loan:           'bg-blue-50 text-blue-700 ring-1 ring-blue-200',
  pge:                 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200',
  shareholder_advance: 'bg-purple-50 text-purple-700 ring-1 ring-purple-200',
  private_investor:    'bg-amber-50 text-amber-700 ring-1 ring-amber-200',
  supplier_credit:     'bg-slate-50 text-slate-600 ring-1 ring-slate-200',
  other:               'bg-slate-50 text-slate-600 ring-1 ring-slate-200',
};

export const REPAYMENT_MODE_LABELS = {
  linear:             'Linéaire',
  in_fine:            'In fine',
  sales_proportional: 'Proportionnel au CA',
};

export const STATUS_LABELS = {
  active:     { label: 'Active',    badge: 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200' },
  paid_off:   { label: 'Soldée',    badge: 'bg-slate-50 text-slate-600 ring-1 ring-slate-200' },
  cancelled:  { label: 'Annulée',   badge: 'bg-red-50 text-red-600 ring-1 ring-red-200' },
};

/**
 * Date de la dernière échéance — même axe que le backend (_periodAxis).
 *
 * `first_payment_date` ancre explicitement le premier paiement (terme échu) ;
 * sans ancre, le premier paiement tombe à start_date + différé (terme à échoir).
 * La date est toujours un offset unique depuis une ancre unique : chaîner les
 * additions dériverait sur les clamps de fin de mois.
 *
 * Renvoie la date du DERNIER prélèvement, cohérente avec le champ « end_date »
 * du formulaire et avec la dernière ligne de l'échéancier.
 *
 * @returns {string|null} 'YYYY-MM-DD'
 */
export function computeEndDate(debt) {
  if (!debt?.start_date || !debt?.duration_months) return null;

  const freq    = Number(debt.payment_frequency_months) || 1;
  const grace   = Number(debt.grace_period_months) || 0;
  const periods = Math.floor(Number(debt.duration_months) / freq);
  if (periods <= 0) return null;

  const anchor       = debt.first_payment_date || debt.start_date;
  const anchorOffset = debt.first_payment_date ? 0 : grace;

  return addMonthsISO(anchor, anchorOffset + (periods - 1) * freq);
}

/**
 * Preview: mensualité/échéance équivalente pour une périodicité donnée.
 * Renvoie le montant d'UNE échéance, pas un montant mensualisé.
 */
export function computePeriodPaymentCents(debt) {
  const freq    = Number(debt?.payment_frequency_months) || 1;
  const periods = Math.floor(Number(debt?.duration_months ?? 0) / freq);
  if (!debt?.capital_cents || periods <= 0) return null;

  // Cf. backend : `/100/12*freq` et non `/100*(freq/12)` — pour freq=1, `x * 1`
  // est l'identité exacte en IEEE754.
  const r = (debt.annual_rate_pct ?? 0) / 100 / 12 * freq;
  const g = Number(debt.grace_period_months) || 0;
  if (r <= 0) return Math.round(debt.capital_cents / periods);

  const capitalAfterGrace = debt.capital_cents * Math.pow(1 + (debt.annual_rate_pct ?? 0) / 100 / 12, g);
  return Math.round(capitalAfterGrace * r / (1 - Math.pow(1 + r, -periods)));
}

export const PAYMENT_FREQUENCY_LABELS = {
  1:  'Mensuelle',
  3:  'Trimestrielle',
  6:  'Semestrielle',
  12: 'Annuelle',
};

/** Nom de l'échéance au singulier, pour les libellés d'UI. */
export const PAYMENT_FREQUENCY_TERM = {
  1:  'mensualité',
  3:  'échéance trimestrielle',
  6:  'échéance semestrielle',
  12: 'échéance annuelle',
};
